import React, { useState, useCallback, useEffect, useRef } from "react";
import { cn } from "@/dentlab/src/lib/util";
import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/dentlab/src/components/ui/button";
import { TabConfig } from "@/dentlab/src/types/tab-config.types";
import { renderIcon } from "@/dentlab/src/pages/job-configuration/utils/icon-mapper";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
import { SIDEBAR_COLLAPSED_WIDTH } from "./job-page.constants";
import { SidebarCollapseToggle } from "./sidebar-collapse-toggle.component";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";

const DEFAULT_EXPANDED_WIDTH = 180;
const MIN_WIDTH = 140;
const MAX_WIDTH = 320;

interface JobInfoTabsPanelProps {
	tabConfig: TabConfig[];
	closedTabs: TabConfig[];
	isCollapsed: boolean;
	onToggleCollapsed: () => void;
	onOpenTab: (tabId: string) => void;
	isFullscreen: boolean;
	onToggleFullscreen: () => void;
	getTabLabel: (tab: TabConfig) => string;
	tabHasIncompleteRequiredFields: (tab: TabConfig) => {
		hasIncomplete: boolean;
		missingFields: string[];
	};
}

export const JobInfoTabsPanel: React.FC<JobInfoTabsPanelProps> = ({
	closedTabs,
	isCollapsed,
	onToggleCollapsed,
	onOpenTab,
	isFullscreen,
	onToggleFullscreen,
	getTabLabel,
	tabHasIncompleteRequiredFields,
}) => {
	const [width, setWidth] = useState(() =>
		getLocalStorage("job-info-tabs-panel-width", DEFAULT_EXPANDED_WIDTH)
	);
	const [isResizing, setIsResizing] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);

	const handleMouseDown = useCallback((e: React.MouseEvent) => {
		setIsResizing(true);
		e.preventDefault();
	}, []);

	const handleMouseMove = useCallback(
		(e: MouseEvent) => {
			if (!isResizing || !panelRef.current) return;

			const rect = panelRef.current.getBoundingClientRect();
			const newWidth = e.clientX - rect.left;
			const clampedWidth = Math.max(
				MIN_WIDTH,
				Math.min(MAX_WIDTH, newWidth)
			);

			setWidth(clampedWidth);
			setLocalStorage("job-info-tabs-panel-width", clampedWidth);
		},
		[isResizing]
	);

	const handleMouseUp = useCallback(() => {
		setIsResizing(false);
	}, []);

	useEffect(() => {
		if (isResizing) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);

			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isResizing, handleMouseMove, handleMouseUp]);

	if (isFullscreen) {
		const sortedClosed = [...closedTabs].sort((a, b) => a.order - b.order);
		return (
			<div
				className="flex flex-col items-center gap-1 pt-2 h-full border-r bg-gray-50 flex-shrink-0 overflow-y-auto"
				style={{ width: SIDEBAR_COLLAPSED_WIDTH }}
				data-testid="job-info-tabs-panel"
			>
				<Button
					variant="ghost"
					size="icon"
					onClick={onToggleFullscreen}
					className="h-6 w-6"
					title="Normale Ansicht"
					data-testid="job-info-tabs-panel-fullscreen"
					aria-label="Normale Ansicht"
				>
					<Minimize2 className="h-3.5 w-3.5 text-gray-600" />
				</Button>
				{sortedClosed.map((tab) => (
					<Tooltip key={tab.id}>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => onOpenTab(tab.id)}
								className="h-6 w-6"
								aria-label={`Tab öffnen: ${getTabLabel(tab)}`}
							>
								{tab.icon ? (
									renderIcon(tab.icon, {
										className: "h-3.5 w-3.5 text-gray-600",
									})
								) : (
									<span className="text-[9px] text-gray-500">
										{getTabLabel(tab).charAt(0)}
									</span>
								)}
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right">
							{getTabLabel(tab)}
						</TooltipContent>
					</Tooltip>
				))}
			</div>
		);
	}

	if (closedTabs.length === 0) {
		return null;
	}

	const sortedClosedTabs = [...closedTabs].sort((a, b) => a.order - b.order);

	return (
		<div
			ref={panelRef}
			className="flex flex-col h-full border-r bg-gray-50 flex-shrink-0 relative"
			style={{ width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : width }}
			data-testid="job-info-tabs-panel"
		>
			{!isCollapsed && (
				<div
					onMouseDown={handleMouseDown}
					className={cn(
						"absolute top-0 right-0 w-1 h-full cursor-col-resize z-10",
						"hover:bg-blue-300 transition-colors",
						isResizing && "bg-blue-400"
					)}
					data-testid="job-info-tabs-panel-resize"
					aria-label="Seitenleiste Größe ändern"
				/>
			)}

			{isCollapsed ? (
				<div className="flex items-center justify-center px-2 py-1.5 border-b">
					<Button
						variant="ghost"
						size="icon"
						onClick={onToggleFullscreen}
						className="h-6 w-6"
						title="Alle Tabs öffnen"
						data-testid="job-info-tabs-panel-fullscreen"
						aria-label="Alle Tabs öffnen"
					>
						<Maximize2 className="h-3.5 w-3.5 text-gray-600" />
					</Button>
				</div>
			) : (
				<div className="flex items-start px-2 py-1 border-b">
					<Button
						variant="ghost"
						size="sm"
						onClick={onToggleFullscreen}
						className="h-7 px-2 gap-1.5 text-xs text-gray-600"
						data-testid="job-info-tabs-panel-fullscreen"
						aria-label="Alle Tabs öffnen"
					>
						<Maximize2 className="h-3.5 w-3.5" />
						Alle Tabs öffnen
					</Button>
				</div>
			)}

			<div className="flex-1 overflow-y-auto py-1">
				{sortedClosedTabs.map((tab) => {
					const incompleteFieldsInfo =
						tabHasIncompleteRequiredFields(tab);
					const label = getTabLabel(tab);

					return (
						<Tooltip key={tab.id}>
							<TooltipTrigger asChild>
								<button
									onClick={() => onOpenTab(tab.id)}
									className={cn(
										"flex items-center w-full py-2",
										"hover:bg-gray-100 transition-colors",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500",
										isCollapsed
											? "justify-center"
											: "px-2 text-left"
									)}
									style={{
										backgroundColor: tab.color
											? `${tab.color}10`
											: undefined,
									}}
									data-testid={`job-info-closed-tab-${tab.id}`}
									aria-label={`Tab öffnen: ${label}`}
								>
									<div className="relative flex-shrink-0">
										{tab.icon ? (
											renderIcon(tab.icon, {
												className:
													"h-5 w-5 text-gray-600",
											})
										) : (
											<span className="flex items-center justify-center h-5 w-5 text-xs font-medium text-gray-500 bg-gray-200 rounded">
												{label.charAt(0).toUpperCase()}
											</span>
										)}
										{incompleteFieldsInfo.hasIncomplete && (
											<div
												className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border border-white"
												title={`Erforderliche Felder: ${incompleteFieldsInfo.missingFields.join(
													", "
												)}`}
											/>
										)}
									</div>
									{!isCollapsed && (
										<span className="ml-2 text-sm text-gray-700 truncate">
											{label}
										</span>
									)}
								</button>
							</TooltipTrigger>
							<TooltipContent side="right">
								<p>{label}</p>
								{incompleteFieldsInfo.hasIncomplete && (
									<p className="text-amber-500 text-xs mt-1">
										Erforderliche Felder:{" "}
										{incompleteFieldsInfo.missingFields.join(
											", "
										)}
									</p>
								)}
							</TooltipContent>
						</Tooltip>
					);
				})}
			</div>

			<SidebarCollapseToggle
				isCollapsed={isCollapsed}
				onToggle={onToggleCollapsed}
				testId="job-info-tabs-panel-toggle"
			/>
		</div>
	);
};
